import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Edit, DeleteOutlined } from '@material-ui/icons';
import { Formik, Form } from 'formik';

import { IIdentity } from 'interfaces/Identity';
import FormNavigationButtons from 'components/FormNavigationButtons';
import { REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { RootState } from 'store';
import {
  createIdentityAction,
  updateIdentityAction,
  setIsCreating,
  deleteIdentityAction,
} from 'modules/identity';

import { IIdentityRegisterProps } from './interfaces';
import IdentityRegisterForm from './IdentityRegisterForm';
import { createIdentityValidationSchema } from './validation';
import { useStyles } from './styles';

const IdentityRegister: React.FC<IIdentityRegisterProps> = ({
  identity,
}: IIdentityRegisterProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootState) => state.identity);
  const [isViewOnly, setIsViewOnly] = useState(identity._id !== 'new');
  const [expanded, setExpanded] = useState(identity._id === 'new');

  const { permissions } = useSelector((state: RootState) => state.user);

  const handleSubmit = (values: IIdentity) => {
    if (identity._id === 'new') {
      dispatch(createIdentityAction(values));
    } else {
      dispatch(updateIdentityAction({ id: identity._id, identity: values }));
    }
  };

  const handleChange = (
    event: React.ChangeEvent<unknown>,
    isExpanded: boolean,
  ) => {
    setExpanded(isExpanded);
    if (!isExpanded) {
      setIsViewOnly(true);
    }
  };

  const handleEdit = (isEditing: boolean, resetForm?: () => void) => (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    setIsViewOnly(!isEditing);
    if (isEditing) {
      setExpanded(true);
    } else {
      resetForm && resetForm();
      if (identity._id === 'new') {
        dispatch(setIsCreating(false));
      }
    }
  };

  const handleDelete = (event: React.ChangeEvent<unknown>) => {
    event.stopPropagation();
    dispatch(deleteIdentityAction(identity._id));
  };

  return (
    <Accordion
      data-cy="identity-register__container--accordion"
      expanded={expanded}
      onChange={isViewOnly ? handleChange : undefined}
    >
      <AccordionSummary data-cy="identity-register__accordion--summary">
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="stretch"
        >
          <Grid item>
            <Typography data-cy="identity-register__label--description">
              {isViewOnly
                ? identity.identity
                : identity._id === 'new'
                ? 'Novo usuário'
                : 'Edição de usuário'}
            </Typography>
          </Grid>
          <Grid item>
            <Box>
              {isViewOnly && permissions['identity:update'] && (
                <IconButton
                  data-cy="identity-register__button--edit"
                  size="small"
                  onClick={handleEdit(true)}
                >
                  <Edit color="primary" />
                </IconButton>
              )}
              {permissions['identity:delete'] && (
                <IconButton
                  data-cy="identity-register__button--remove"
                  size="small"
                  onClick={handleDelete}
                >
                  <DeleteOutlined color="error" />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </AccordionSummary>
      {expanded && (
        <AccordionDetails data-cy="identity-register__accordion--details">
          <Formik
            initialValues={identity}
            onSubmit={handleSubmit}
            validationSchema={createIdentityValidationSchema}
          >
            {({ resetForm }) => (
              <Form className={classes.form}>
                <IdentityRegisterForm isViewOnly={isViewOnly} />
                {!isViewOnly && (
                  <FormNavigationButtons
                    isViewOnly={isViewOnly}
                    backButtonOnClick={handleEdit(false, resetForm)}
                    submitButtonLabel="Salvar"
                    backButtonLabel="Cancelar"
                    isSubmitting={loading === REQUEST_LIFE_CICLE.PENDING}
                  />
                )}
              </Form>
            )}
          </Formik>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default IdentityRegister;
